(() => {
  'use strict';

  const KLARNA_SELECTOR = 'klarna-placement';
  const AFTERPAY_SELECTOR = 'afterpay-placement';
  const CONFIG_SELECTOR = '[data-hik-klarna-config], [data-hik-product-payment-native]';
  const ACTION_SELECTOR = 'a, button, [role="link"], [role="button"]';
  const ACTION_TEXT = /purchase\s*power|check\s*purchase|learn\s*more/i;

  const DEFAULT_AFTERPAY_SDK_SRC = 'https://js.afterpay.com/afterpay-1.x.js';
  const DEFAULT_KLARNA_SDK_SRC = 'https://js.klarna.com/web-sdk/v1/klarna.js';
  const DEFAULT_KLARNA_CLIENT_ID = 'klarna_live_client_ckhnIzBRL09xRihLalczN1I5MC9aRVZKNnJLaXIlV2osMjQwOTJlZDItNjEyMS00NzlmLWJhYjAtZWQ2YzdlZDY5NGM5LDEsbFllYVlOa0ZraHpvK2JNd1ZhWDVaL29ndjI3cWl0WlJsR0twUnU3bXF3UT0';

  let afterpaySdkPromise = null;
  let klarnaSdkPromise = null;

  const getActionFromRoot = (root) => {
    if (!root || typeof root.querySelectorAll !== 'function') return null;
    const actions = Array.from(root.querySelectorAll(ACTION_SELECTOR));
    return actions.find((node) => ACTION_TEXT.test((node.textContent || '').trim()))
      || actions.find((node) => node.tagName === 'A' && node.href)
      || actions.find((node) => node.tagName === 'BUTTON')
      || null;
  };

  const getKlarnaAction = (placement) => {
    return getActionFromRoot(placement.shadowRoot) || getActionFromRoot(placement);
  };

  const readKlarnaConfig = (node) => ({
    src: (node?.dataset?.hikKlarnaSdkSrc || '').trim() || DEFAULT_KLARNA_SDK_SRC,
    clientId: (node?.dataset?.hikKlarnaClientId || '').trim() || DEFAULT_KLARNA_CLIENT_ID,
  });

  const readAfterpayConfig = (node) => ({
    src: (node?.dataset?.hikAfterpaySdkSrc || '').trim() || DEFAULT_AFTERPAY_SDK_SRC,
  });

  const ensureAfterpaySdk = (config = {}) => {
    const src = (config.src || DEFAULT_AFTERPAY_SDK_SRC).trim();

    const exactExisting = Array.from(document.scripts).find((script) => {
      const scriptSrc = script.getAttribute('src') || '';
      return script.dataset.hikAfterpaySdk === 'true' || scriptSrc === src;
    });
    if (exactExisting) return Promise.resolve(exactExisting);

    if (afterpaySdkPromise) return afterpaySdkPromise;

    afterpaySdkPromise = new Promise((resolve, reject) => {
      const script = document.createElement('script');
      script.async = true;
      script.src = src;
      script.dataset.hikAfterpaySdk = 'true';
      script.setAttribute('data-analytics-enabled', '');
      script.addEventListener('load', () => resolve(script), { once: true });
      script.addEventListener('error', () => {
        afterpaySdkPromise = null;
        reject(new Error('Afterpay SDK failed to load'));
      }, { once: true });
      document.head.appendChild(script);
    });

    return afterpaySdkPromise;
  };

  const refreshKlarna = () => {
    try { window.Klarna?.OnsiteMessaging?.refresh?.(); } catch (_) {}
  };

  const ensureKlarnaSdk = (config = {}) => {
    const src = (config.src || DEFAULT_KLARNA_SDK_SRC).trim();
    const clientId = (config.clientId || DEFAULT_KLARNA_CLIENT_ID).trim();

    if (window.Klarna) {
      refreshKlarna();
      return Promise.resolve(window.Klarna);
    }

    const existing = Array.from(document.scripts).find((script) => {
      const scriptSrc = script.getAttribute('src') || '';
      return script.dataset.hikKlarnaSdk === 'true'
        || scriptSrc.includes('js.klarna.com/web-sdk/')
        || scriptSrc === src;
    });

    if (existing) {
      if (!existing.getAttribute('data-client-id') && clientId) {
        existing.setAttribute('data-client-id', clientId);
      }
      return new Promise((resolve) => {
        if (window.Klarna) return resolve(window.Klarna);
        existing.addEventListener('load', () => {
          refreshKlarna();
          resolve(window.Klarna);
        }, { once: true });
        window.setTimeout(() => resolve(window.Klarna), 2500);
      });
    }

    if (klarnaSdkPromise) return klarnaSdkPromise;

    klarnaSdkPromise = new Promise((resolve, reject) => {
      const script = document.createElement('script');
      script.async = true;
      script.src = src;
      script.dataset.environment = 'production';
      script.dataset.hikKlarnaSdk = 'true';
      if (clientId) script.setAttribute('data-client-id', clientId);
      script.addEventListener('load', () => {
        refreshKlarna();
        resolve(window.Klarna);
      }, { once: true });
      script.addEventListener('error', () => {
        klarnaSdkPromise = null;
        reject(new Error('Klarna SDK failed to load'));
      }, { once: true });
      document.head.appendChild(script);
    });

    return klarnaSdkPromise;
  };

  // Preserve original Klarna behavior: clicking the visible placement forwards
  // to Klarna's own CTA, so Klarna opens its native informational popup.
  const enhanceKlarnaPlacement = (placement) => {
    if (!placement || placement.dataset.hikWholeClick === 'true') return;
    placement.dataset.hikWholeClick = 'true';
    placement.style.cursor = 'pointer';

    placement.addEventListener('click', (event) => {
      if (placement.dataset.hikForwarding === 'true') return;

      const path = typeof event.composedPath === 'function' ? event.composedPath() : [];
      const clickedNativeAction = path.some((node) => {
        if (!node || node === placement || typeof node.matches !== 'function') return false;
        return node.matches(ACTION_SELECTOR);
      });
      if (clickedNativeAction) return;

      const action = getKlarnaAction(placement);
      if (!action) return;

      event.preventDefault();
      placement.dataset.hikForwarding = 'true';
      try {
        action.click();
      } finally {
        window.setTimeout(() => delete placement.dataset.hikForwarding, 0);
      }
    });
  };

  const applyProductNativeConfig = (scope = document) => {
    const roots = [];
    if (scope.matches?.('[data-hik-product-payment-native]')) roots.push(scope);
    scope.querySelectorAll?.('[data-hik-product-payment-native]').forEach((root) => roots.push(root));

    roots.forEach((root) => {
      const klarnaKey = root.dataset.hikKlarnaKey || '';
      const klarnaLocale = root.dataset.hikKlarnaLocale || '';
      const afterpayLocale = root.dataset.hikAfterpayLocale || '';
      const afterpayCurrency = root.dataset.hikAfterpayCurrency || '';

      ensureAfterpaySdk(readAfterpayConfig(root)).catch(() => {});
      ensureKlarnaSdk(readKlarnaConfig(root)).then(refreshKlarna).catch(() => {});

      root.querySelectorAll(KLARNA_SELECTOR).forEach((placement) => {
        if (klarnaKey) placement.setAttribute('data-key', klarnaKey);
        if (klarnaLocale) placement.setAttribute('data-locale', klarnaLocale);
      });

      root.querySelectorAll(AFTERPAY_SELECTOR).forEach((placement) => {
        if (afterpayLocale) placement.setAttribute('data-locale', afterpayLocale);
        if (afterpayCurrency) placement.setAttribute('data-currency', afterpayCurrency);
      });
    });
  };

  const applyHomepageKlarnaConfig = (scope = document) => {
    const configs = [];
    if (scope.matches?.('[data-hik-klarna-config]')) configs.push(scope);
    scope.querySelectorAll?.('[data-hik-klarna-config]').forEach((node) => configs.push(node));
    configs.forEach((node) => ensureKlarnaSdk(readKlarnaConfig(node)).then(refreshKlarna).catch(() => {}));
  };

  const scan = (scope = document) => {
    applyHomepageKlarnaConfig(scope);
    applyProductNativeConfig(scope);

    if (scope.matches?.(KLARNA_SELECTOR)) enhanceKlarnaPlacement(scope);
    scope.querySelectorAll?.(KLARNA_SELECTOR).forEach(enhanceKlarnaPlacement);

    const hasKlarnaPlacement = scope.matches?.(KLARNA_SELECTOR) || scope.querySelector?.(KLARNA_SELECTOR);
    if (hasKlarnaPlacement && !document.querySelector(CONFIG_SELECTOR)) {
      ensureKlarnaSdk().then(refreshKlarna).catch(() => {});
    }

    const hasAfterpayPlacement = scope.matches?.(AFTERPAY_SELECTOR) || scope.querySelector?.(AFTERPAY_SELECTOR);
    if (hasAfterpayPlacement && !document.querySelector('[data-hik-product-payment-native]')) {
      ensureAfterpaySdk().catch(() => {});
    }
  };

  const start = () => {
    scan();

    const observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        mutation.addedNodes.forEach((node) => {
          if (node.nodeType === 1) scan(node);
        });
      });
    });
    observer.observe(document.documentElement, { childList: true, subtree: true });

    document.addEventListener('shopify:section:load', (event) => scan(event.target));
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start, { once: true });
  } else {
    start();
  }
})();
